import {
  PhoneNumberUtil,
  PhoneNumberFormat,
  PhoneNumber,
} from "google-libphonenumber";
import { useState, useRef, useEffect } from "react";

import { phoneOnChangeType } from "../../Input/types";
import { CountryCode, CountryCodes } from "../utils/countryCodes";

const phoneUtil = PhoneNumberUtil.getInstance();

// Utility functions for phone number parsing with libphonenumber
const parsePhoneNumber = (
  input: string,
  defaultRegion?: string
): {
  countryCode?: string;
  nationalNumber?: string;
  isValid: boolean;
  parsedNumber?: PhoneNumber;
} => {
  if (!input || input.trim() === "") {
    return { isValid: false };
  }

  try {
    // Try to parse the input as a phone number
    const parsedNumber = phoneUtil.parseAndKeepRawInput(
      input,
      defaultRegion || "US"
    );

    if (phoneUtil.isValidNumber(parsedNumber)) {
      const countryCode = parsedNumber.getCountryCode()?.toString();
      const nationalNumber = parsedNumber.getNationalNumber()?.toString();

      return {
        countryCode,
        nationalNumber,
        isValid: true,
        parsedNumber,
      };
    }
  } catch (error) {
    // If parsing fails, try to extract country code manually
    const cleanInput = input.replace(/\D/g, "");
    if (cleanInput.length > 0) {
      return {
        nationalNumber: cleanInput,
        isValid: false,
      };
    }
  }

  return { isValid: false };
};

const findCountryByCode = (countryCode: string): CountryCode | undefined => {
  const codeNumber = parseInt(countryCode, 10);
  return CountryCodes.find((country) => country.code === codeNumber);
};

const extractCountryCodeFromInput = (
  input: string
): {
  countryCode?: CountryCode;
  remainingInput: string;
} => {
  if (!input || !input.includes("+")) {
    return { remainingInput: input };
  }

  // Remove the + and try to parse
  const withoutPlus = input.substring(1);

  // Try different country code lengths (1-4 digits)
  for (let length = 4; length >= 1; length--) {
    const potentialCode = withoutPlus.substring(0, length);
    const country = findCountryByCode(potentialCode);

    if (country) {
      const remainingInput = withoutPlus.substring(length);
      return { countryCode: country, remainingInput };
    }
  }

  return { remainingInput: input };
};

const findCountryCode = (id?: string): CountryCode => {
  return id
    ? CountryCodes.find((countryCode) => countryCode.id === id) ||
        CountryCodes.find(
          (countryCode) => countryCode.code.toString() === id
        ) ||
        CountryCodes[0]
    : CountryCodes[0];
};

export const usePhoneInput = ({
  onChange,
  defaultCountryId,
  onCountryCodeChange,
}: {
  onChange: phoneOnChangeType;
  defaultCountryId?: string;
  onCountryCodeChange?: (code: string) => void;
}) => {
  const [countryCode, setCountryCode] = useState<CountryCode>(
    findCountryCode(defaultCountryId)
  );

  useEffect(() => {
    setCountryCode(findCountryCode(defaultCountryId));
  }, [defaultCountryId]);

  const [addingCC, setAddingCC] = useState<boolean>(false);
  const countryCodeTimeoutRef = useRef<null | ReturnType<typeof setTimeout>>(
    null
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (countryCodeTimeoutRef.current) {
        clearTimeout(countryCodeTimeoutRef.current);
      }
    };
  }, []);

  const handleChange = (val: string) => {
    // Check if user is typing a country code (starts with +)
    if (val.includes("+")) {
      setAddingCC(true);
    }

    if (addingCC) {
      // Use the new country code detection logic
      const countryCodeResult = handleCountryCodeInput(val);

      if (countryCodeResult.shouldClear && countryCodeResult.newCountryCode) {
        // Use the helper function to reset the input state
        resetInputForNewCountryCode(countryCodeResult.newCountryCode, val);
        setAddingCC(false);

        return; // Exit early since we've handled the country code change
      }
    }

    // Try to parse the input using libphonenumber
    const parseResult = parsePhoneNumber(val, countryCode.regionCode);

    if (
      parseResult.isValid &&
      parseResult.countryCode &&
      parseResult.nationalNumber
    ) {
      // If we successfully parsed a complete phone number, update the country code
      const detectedCountry = findCountryByCode(parseResult.countryCode);
      if (detectedCountry && detectedCountry.id !== countryCode.id) {
        setCountryCode(detectedCountry);
        if (onCountryCodeChange) {
          onCountryCodeChange(detectedCountry.code.toString());
        }
      }

      onChange({
        countryCode: parseResult.countryCode,
        nationalNumber: parseResult.nationalNumber,
      });
    } else {
      // Fallback to the original logic for partial input
      const numbers = val.includes("+") ? val : val.replace(/\D/g, "");

      onChange({
        countryCode: countryCode.code.toString(),
        nationalNumber: numbers,
      });
    }
  };

  const handleCountryCodeInput = (
    input: string
  ): { shouldClear: boolean; newCountryCode?: CountryCode } => {
    if (!input) {
      return { shouldClear: false };
    }

    // Clear any existing timeout
    if (countryCodeTimeoutRef.current) {
      clearTimeout(countryCodeTimeoutRef.current);
    }

    // Use the new libphonenumber-based country code extraction
    const extractionResult = extractCountryCodeFromInput(input);

    if (extractionResult.countryCode) {
      // We found a valid country code
      const matchedCountry = extractionResult.countryCode;

      // Check if this is a complete country code match
      const inputWithoutPlus = input.substring(1);
      const isExactMatch = inputWithoutPlus.startsWith(
        matchedCountry.code.toString()
      );

      if (isExactMatch) {
        // For exact matches, switch immediately
        return {
          shouldClear: true,
          newCountryCode: matchedCountry,
        };
      } else {
        // For partial matches, wait a bit to see if the user continues typing
        // This prevents switching to Germany when typing +4 for +44
        countryCodeTimeoutRef.current = setTimeout(() => {
          if (addingCC) {
            resetInputForNewCountryCode(matchedCountry, input);
            setAddingCC(false);
          }
        }, 1000); // Wait 1 second before switching

        return { shouldClear: false };
      }
    }

    // Fallback to the original logic for edge cases
    const cleanInput = input.replace(/\D/g, "");
    if (!cleanInput) {
      return { shouldClear: false };
    }

    // Find all countries that match the input (exact or partial)
    const allMatches = CountryCodes.filter((country) =>
      country.code.toString().startsWith(cleanInput)
    );

    if (allMatches.length > 0) {
      // If we have multiple matches, apply preferred country logic
      if (allMatches.length > 1) {
        allMatches.sort(
          (a, b) => b.code.toString().length - a.code.toString().length
        );

        // If we have multiple matches, prefer the one that's most commonly used
        const preferredCountries = ["US", "GB", "DE", "FR", "AU"];
        const preferredMatch = allMatches.find((country) =>
          preferredCountries.includes(country.id)
        );

        const matchedCountry = preferredMatch || allMatches[0];

        // Only switch immediately if it's an exact match
        if (matchedCountry.code.toString() === cleanInput) {
          return {
            shouldClear: true,
            newCountryCode: matchedCountry,
          };
        }

        // For partial matches, wait a bit to see if the user continues typing
        countryCodeTimeoutRef.current = setTimeout(() => {
          if (addingCC) {
            resetInputForNewCountryCode(matchedCountry, input);
            setAddingCC(false);
          }
        }, 1000);

        return { shouldClear: false };
      } else {
        // Only one match, use it
        const matchedCountry = allMatches[0];

        // Only switch immediately if it's an exact match
        if (matchedCountry.code.toString() === cleanInput) {
          return {
            shouldClear: true,
            newCountryCode: matchedCountry,
          };
        }

        // For partial matches with single country, wait a bit
        countryCodeTimeoutRef.current = setTimeout(() => {
          if (addingCC) {
            resetInputForNewCountryCode(matchedCountry, input);
            setAddingCC(false);
          }
        }, 800);

        return { shouldClear: false };
      }
    }

    return { shouldClear: false };
  };

  const resetInputForNewCountryCode = (
    newCountryCode: CountryCode,
    input: string
  ) => {
    const inputWithoutPlus = input.substring(1);
    const numbers = inputWithoutPlus.includes("+")
      ? inputWithoutPlus
      : inputWithoutPlus.replace(/\D/g, "");

    // Clear any pending timeout
    if (countryCodeTimeoutRef.current) {
      clearTimeout(countryCodeTimeoutRef.current);
      countryCodeTimeoutRef.current = null;
    }

    setCountryCode(newCountryCode);
    setAddingCC(false);

    const newCountryCodeString = newCountryCode.code.toString();

    const localNumber = numbers
      .substring(0, newCountryCodeString.length)
      .includes(newCountryCodeString)
      ? numbers.substring(newCountryCodeString.length, numbers.length)
      : numbers;

    // Call the onChange with the new country code and empty national number
    onChange({
      countryCode: newCountryCode.code.toString(),
      nationalNumber: localNumber,
    });

    // Also call the onCountryCodeChange callback if provided
    if (onCountryCodeChange) {
      onCountryCodeChange(newCountryCode.code.toString());
    }
  };

  const handleCountryCodeChange = (code: CountryCode) => {
    // Clear any pending timeout when manually changing country code
    if (countryCodeTimeoutRef.current) {
      clearTimeout(countryCodeTimeoutRef.current);
      countryCodeTimeoutRef.current = null;
    }

    setCountryCode(code);
    if (onCountryCodeChange) {
      onCountryCodeChange(code.code.toString());
    }
  };

  const maskPhoneNumber = (
    number: string | undefined | null,
    regionCode?: string
  ) => {
    if (!number || !number.length) {
      return number;
    }

    try {
      // Try to parse the number using libphonenumber
      const parsedNumber = phoneUtil.parseAndKeepRawInput(
        number,
        regionCode || "US"
      );

      if (phoneUtil.isValidNumber(parsedNumber)) {
        return phoneUtil.format(parsedNumber, PhoneNumberFormat.NATIONAL);
      } else {
        // If not valid, try to format as national number with current country
        const currentRegion = regionCode || countryCode.regionCode;
        const fallbackParsed = phoneUtil.parseAndKeepRawInput(
          number,
          currentRegion
        );
        return phoneUtil.format(fallbackParsed, PhoneNumberFormat.NATIONAL);
      }
    } catch (e) {
      // If all parsing fails, return the original number
      return number;
    }
  };

  return {
    countryCode,
    handleCountryCodeChange,
    handleChange,
    handleCountryCodeInput,
    resetInputForNewCountryCode,
    maskPhoneNumber,
  };
};
